/**
 * NotificationDropdown - Rich dropdown menu for bell notifications
 *
 * Displays notification list with actions for read/unread management
 * Uses MUI Menu, List, and other components for consistent styling
 *
 * A server-sourced row carries a `deepLinkPath` and a `sourceId`: activating it
 * navigates in-app, and read/dismiss are mirrored to the API. Locally-produced
 * rows have neither and keep their existing behaviour.
 */

import React, { useCallback } from 'react'
import { useNavigate } from 'react-router'
import { Notifications } from '@mui/icons-material'
import { Button, Divider, Menu, Typography } from '@mui/material'
import { formatDistanceToNow } from 'date-fns'
import { AlertTriangle, Mail, Trash2 } from 'lucide-react'

import { useMessaging } from '../../contexts/MessagingContext'
import { useTenant } from '../../contexts/TenantContext'
import {
  dismissAiSecurityNotification,
  dismissAllAiSecurityNotifications,
  markAiSecurityNotificationRead,
  markAllAiSecurityNotificationsRead,
} from '../../services/api/aiSecurityNotifications'
import type { Message, MessageType } from '../../types/messaging'
import { MESSAGE_TYPE_CONFIG } from '../../types/messaging'

import { messageTypeIcon } from './messageIcons'
import { formatUnreadCount } from './unreadCount'

interface NotificationDropdownProps {
  anchorEl: HTMLElement | null
  open: boolean
  onClose: () => void
}

/** Accessible name for the row's link region. Drops the relative time, which
 *  re-renders every minute and is not what the link goes to. */
const activationLabelFor = (message: Message): string =>
  message.title
    ? `Open notification: ${message.title}. ${message.message}`
    : `Open notification: ${message.message}`

interface NotificationItemProps {
  message: Message
  /** Text region activated by click / Enter / Space. Only wired when the
   *  message carries a `deepLinkPath`. */
  onActivate: (message: Message) => void
  onMarkAsRead: (message: Message) => void
  onRemove: (message: Message) => void
  onCommandClick: (command: () => void) => void
}

const NotificationItem = ({
  message,
  onActivate,
  onMarkAsRead,
  onRemove,
  onCommandClick,
}: NotificationItemProps): React.JSX.Element => {
  // Glyph comes from the shared severity map so the bell row and the toast
  // for the same message agree — see `messageIcons.ts`. Colour comes from the
  // same per-type token the rest of the messaging surface uses (already a
  // light/dark pair, and it matches the row's left severity border), so at
  // 16px severity is carried by hue as well as shape.
  const SeverityIcon = messageTypeIcon(message.type)
  const iconClass = `w-4 h-4 ${MESSAGE_TYPE_CONFIG[message.type].iconColor}`

  const getSeverityBorderClass = (type: MessageType) => {
    switch (type) {
      case 'success':
        return 'border-l-green-500'
      case 'error':
      case 'failure':
        return 'border-l-red-500'
      case 'warning':
        return 'border-l-amber-500'
      case 'info':
        return 'border-l-blue-500'
      default: {
        // Compile-time: a new union member fails to typecheck here.
        // Runtime: a wire value outside the union still degrades safely.
        const _exhaustive: never = type
        return 'border-l-blue-500'
      }
    }
  }

  const formatTime = (timestamp: Date | undefined): string => {
    if (!timestamp) return 'Unknown time'
    try {
      return formatDistanceToNow(timestamp, { addSuffix: true })
    } catch {
      return 'Unknown time'
    }
  }

  // The three handlers below `stopPropagation` because MUI's Menu also listens
  // above these nodes, and because it keeps clicking Trash from navigating to
  // the row it just deleted should the layout ever be nested inside the link
  // region.
  const handleMarkAsRead = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation()
      if (message.id && !message.read) {
        onMarkAsRead(message)
      }
    },
    [message, onMarkAsRead],
  )

  const handleRemove = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation()
      if (message.id) {
        onRemove(message)
      }
    },
    [message, onRemove],
  )

  const handleCommandClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation()
      if (message.command) {
        onCommandClick(message.command.onClick)
      }
    },
    [message.command, onCommandClick],
  )

  // Only server-resolved rows have somewhere to go. A locally-produced message
  // has no `deepLinkPath` and keeps today's behaviour: no click target, no role,
  // no tab stop, no pointer cursor.
  const isNavigable = Boolean(message.deepLinkPath)
  const activationLabel = isNavigable ? activationLabelFor(message) : undefined

  const handleActivate = useCallback(() => {
    onActivate(message)
  }, [message, onActivate])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (event.key !== 'Enter' && event.key !== ' ') return
      // Space would otherwise scroll the menu.
      event.preventDefault()
      onActivate(message)
    },
    [message, onActivate],
  )

  return (
    <div
      className={`px-4 py-3 border-l-[3px] hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
        message.read
          ? 'border-l-transparent'
          : getSeverityBorderClass(message.type)
      }`}
      data-testid="notification-item"
      data-unread={!message.read}
      data-severity={message.type}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className="flex-shrink-0 mt-0.5" data-testid="notification-icon">
          <SeverityIcon className={iconClass} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Text region — the row's ONE interactive element. The mark-read /
              remove / command controls must stay siblings of it, never
              descendants: interactive-inside-interactive is invalid ARIA and
              takes the tab stop ahead of the row's own buttons. */}
          <div
            className={isNavigable ? 'cursor-pointer' : undefined}
            onClick={isNavigable ? handleActivate : undefined}
            onKeyDown={isNavigable ? handleKeyDown : undefined}
            role={isNavigable ? 'link' : undefined}
            tabIndex={isNavigable ? 0 : undefined}
            aria-label={activationLabel}
            data-testid="notification-text"
          >
            {/* Title and timestamp row */}
            <div className="flex items-start justify-between gap-2 mb-1">
              {message.title && (
                <Typography
                  variant="subtitle2"
                  className="font-medium truncate text-gray-900 dark:text-white"
                >
                  {message.title}
                </Typography>
              )}
              <Typography
                variant="caption"
                className="text-gray-500 dark:text-white whitespace-nowrap text-xs italic"
              >
                {formatTime(message.timestamp)}
              </Typography>
            </div>

            {/* Message content */}
            <Typography
              variant="body2"
              className="mb-2 text-sm leading-relaxed text-gray-700 dark:text-white"
            >
              {message.message}
            </Typography>
          </div>

          {/* Command and actions row */}
          <div className="flex items-center justify-between gap-2">
            {message.command && (
              <Button
                size="small"
                variant="text"
                onClick={handleCommandClick}
                className="text-blue-600 hover:text-blue-700 dark:text-white dark:hover:text-gray-200 p-0 min-w-0 text-xs normal-case"
                data-testid="notification-command"
              >
                {message.command.text}
              </Button>
            )}

            <div className="flex items-center gap-1 ml-auto">
              {!message.read && (
                <button
                  onClick={handleMarkAsRead}
                  className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title="Mark as read"
                  data-testid="notification-mark-read"
                >
                  <Mail className="w-3.5 h-3.5 text-gray-500 dark:text-white" />
                </button>
              )}
              <button
                onClick={handleRemove}
                className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                title="Remove"
                data-testid="notification-remove"
              >
                <Trash2 className="w-3.5 h-3.5 text-gray-500 dark:text-white" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * NotificationDropdown - Main dropdown component
 */
// @purpose Notification bell dropdown listing bell messages with deep-link row navigation and mark-read/dismiss/clear-all actions mirrored to the AI-security notifications API
export const NotificationDropdown = ({
  anchorEl,
  open,
  onClose,
}: NotificationDropdownProps): React.JSX.Element => {
  const {
    bellMessages,
    unreadCount,
    persistenceWarning,
    markAsRead,
    markAllAsRead,
    removeMessage,
    clearBellMessages,
    noteDismissedSourceIds,
    showError,
  } = useMessaging()
  const { selectedTenant } = useTenant()
  const navigate = useNavigate()

  const tenantId = selectedTenant?.id

  // The sweeps are tenant-wide, so they are worth firing whenever ANY row came
  // from the server — the poll caps at 100 rows, so the server's set can be
  // larger than what is on screen. With only local rows there is nothing to
  // sweep, and the caller may not hold the read role the endpoints require.
  const hasServerBackedRows = bellMessages.some(
    msg => msg.sourceId !== undefined,
  )

  /**
   * Mirrors of local changes to the API are fired un-awaited — the ≤60 s poll is
   * the authority and re-derives the truth. Failures are always logged; the ones
   * whose local effect visibly *reverts* on the next poll also raise a toast.
   *
   * `showError` writes a `target: 'toast'` message, never a bell row, so it
   * cannot be dismissed/cleared and re-enter this path.
   */
  const reportMutationFailure = useCallback(
    (action: string, userMessage: string | null) =>
      (error: unknown): void => {
        console.error(`AI security notifications: ${action} failed`, error)
        if (userMessage) showError(userMessage)
      },
    [showError],
  )

  const markReadOnServer = useCallback(
    (sourceId: number | undefined): void => {
      if (!tenantId || sourceId === undefined) return
      void markAiSecurityNotificationRead(tenantId, sourceId).catch(
        // Log-only: the user asked to navigate, not to mark read, and the poll
        // re-asserts read state within 60 s.
        reportMutationFailure('mark read', null),
      )
    },
    [tenantId, reportMutationFailure],
  )

  const handleActivate = useCallback(
    (message: Message) => {
      const path = message.deepLinkPath
      if (!path) return
      if (message.id && !message.read) {
        markAsRead(message.id)
      }
      markReadOnServer(message.sourceId)
      navigate(path)
      // The bell lives in the header, so an open menu would hang over the page
      // we just navigated to.
      onClose()
    },
    [markAsRead, markReadOnServer, navigate, onClose],
  )

  const handleMarkAsRead = useCallback(
    (message: Message) => {
      if (message.id) {
        markAsRead(message.id)
      }
      markReadOnServer(message.sourceId)
    },
    [markAsRead, markReadOnServer],
  )

  const handleRemove = useCallback(
    (message: Message) => {
      // A locally-produced row has no server identity to dismiss.
      if (message.sourceId !== undefined) {
        // A GET issued moments ago still carries this row; suppress it so the
        // reconcile doesn't re-add it. STICKY: holds across every poll that
        // still returns it, pruned only once the server confirms the drop.
        noteDismissedSourceIds([message.sourceId])
        if (tenantId) {
          void dismissAiSecurityNotification(tenantId, message.sourceId).catch(
            reportMutationFailure(
              'dismiss',
              'Could not dismiss that notification. It may reappear shortly.',
            ),
          )
        }
      }
      if (message.id) {
        removeMessage(message.id)
      }
    },
    [tenantId, removeMessage, noteDismissedSourceIds, reportMutationFailure],
  )

  const handleMarkAllAsRead = useCallback(() => {
    if (tenantId && hasServerBackedRows) {
      void markAllAiSecurityNotificationsRead(tenantId).catch(
        reportMutationFailure(
          'mark all read',
          'Could not mark all notifications as read. Some may reappear as unread.',
        ),
      )
    }
    markAllAsRead()
  }, [tenantId, hasServerBackedRows, markAllAsRead, reportMutationFailure])

  const handleClearAll = useCallback(() => {
    // Same in-flight-GET race as a single dismiss, for every row at once.
    noteDismissedSourceIds(
      bellMessages
        .map(msg => msg.sourceId)
        .filter((sourceId): sourceId is number => sourceId !== undefined),
    )
    if (tenantId && hasServerBackedRows) {
      // Without the server-side sweep the list resurrects on the next poll.
      void dismissAllAiSecurityNotifications(tenantId).catch(
        reportMutationFailure(
          'clear all',
          'Could not clear notifications. Some may reappear shortly.',
        ),
      )
    }
    clearBellMessages()
    onClose()
  }, [
    tenantId,
    hasServerBackedRows,
    bellMessages,
    clearBellMessages,
    noteDismissedSourceIds,
    onClose,
    reportMutationFailure,
  ])

  const handleCommandClick = useCallback(
    (command: () => void) => {
      command()
      onClose() // Close dropdown after command execution
    },
    [onClose],
  )

  // Sort messages by timestamp (newest first)
  const sortedMessages = [...bellMessages].sort((a, b) => {
    if (!a.timestamp || !b.timestamp) return 0
    return b.timestamp.getTime() - a.timestamp.getTime()
  })

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      PaperProps={{
        elevation: 8,
        sx: {
          width: 380,
          maxHeight: 480,
          overflow: 'visible',
          filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
          mt: 1.5,
          '&:before': {
            content: '""',
            display: 'block',
            position: 'absolute',
            top: 0,
            right: 28,
            width: 10,
            height: 10,
            bgcolor: 'background.paper',
            transform: 'translateY(-50%) rotate(45deg)',
            zIndex: 0,
          },
        },
      }}
      data-testid="notification-dropdown"
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <Typography
            variant="h6"
            className="font-semibold text-gray-900 dark:text-white"
          >
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <div
              className="px-2 py-1 bg-gray-900/10 text-gray-700 dark:bg-white/20 dark:text-white text-xs font-medium rounded-full"
              data-testid="notification-unread-summary"
            >
              {formatUnreadCount(unreadCount)} unread
            </div>
          )}
        </div>
      </div>

      {/* Local-cache warning. Advisory: the notifications on screen are all
          still here, only the browser-side copy was dropped. */}
      {persistenceWarning && (
        <div
          className="px-4 py-2 flex items-start gap-2 border-b border-amber-300 bg-amber-50 dark:border-amber-400/40 dark:bg-amber-400/10"
          role="status"
          data-testid="notification-persistence-warning"
        >
          <AlertTriangle className="w-3.5 h-3.5 text-amber-600 dark:text-amber-200 flex-shrink-0 mt-0.5" />
          <Typography
            variant="caption"
            className="text-amber-800 dark:text-amber-100"
          >
            {persistenceWarning}
          </Typography>
        </div>
      )}

      {/* Content */}
      <div className="max-h-80 overflow-y-auto">
        {sortedMessages.length === 0 ? (
          /* Empty State */
          <div className="px-4 py-8 text-center">
            {/* Same glyph as the bell that opens this panel — see NotificationBell.
                Centred by the parent's `text-center`, like the Typography siblings:
                MUI's own `inline-block` wins over `block`, and `mx-auto` is a no-op
                on an inline-level box. */}
            <Notifications
              sx={{ fontSize: 48 }}
              className="mb-3 text-gray-400 dark:text-white"
            />
            <Typography
              variant="body2"
              className="text-gray-700 dark:text-white mb-1"
            >
              No notifications
            </Typography>
            <Typography
              variant="caption"
              className="text-gray-500 dark:text-white/80"
            >
              You're all caught up!
            </Typography>
          </div>
        ) : (
          /* Notification Items */
          <div className="relative">
            {sortedMessages.map((message, index) => (
              <div key={message.id || index}>
                <NotificationItem
                  message={message}
                  onActivate={handleActivate}
                  onMarkAsRead={handleMarkAsRead}
                  onRemove={handleRemove}
                  onCommandClick={handleCommandClick}
                />
                {index < sortedMessages.length - 1 && (
                  <Divider className="mx-4" />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer Actions — split into two sibling conditionals rather than
          one Fragment. MUI's Menu iterates its direct children with
          React.Children.forEach and warns whenever any is a Fragment, since
          a Fragment breaks the arrow-key focus walk it derives from that
          list. Two conditionals evaluating the same predicate produce two
          top-level children the Menu can walk over. */}
      {sortedMessages.length > 0 && <Divider />}
      {sortedMessages.length > 0 && (
        <div className="px-4 py-3 flex gap-2 border-t-slate-200 dark:border-t-slate-600 border-t">
          {unreadCount > 0 && (
            <Button
              size="small"
              variant="text"
              onClick={handleMarkAllAsRead}
              startIcon={<Mail className="w-4 h-4" />}
              className="text-gray-700 hover:text-gray-900 dark:text-white dark:hover:text-gray-200"
              data-testid="notification-mark-all-read"
            >
              Mark all read
            </Button>
          )}
          <Button
            size="small"
            variant="text"
            onClick={handleClearAll}
            startIcon={<Trash2 className="w-4 h-4" />}
            className="text-gray-700 hover:text-gray-900 dark:text-white dark:hover:text-gray-200 ml-auto"
            data-testid="notification-clear-all"
          >
            Clear all
          </Button>
        </div>
      )}
    </Menu>
  )
}

export default NotificationDropdown
